import {
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import { TruckIcon as TruckSolid } from '@heroicons/react/24/solid';
import { format } from 'date-fns';
import { useState } from 'react';

import { cn } from '@/lib/utils';
import type { CarrierTrackingData } from '@/services/api/generated/postgridService';

interface JourneyMapProps {
  carrierTracking?: CarrierTrackingData | null;
  trackingNumber?: string | null;
  className?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getLoc = (u: any) => {
  const l = u.trackingLocation || u.tracking_location;
  return l
    ? {
        city: l.city,
        state: l.provinceOrState || l.province_or_state,
        zip: l.postalOrZip || l.postal_or_zip,
      }
    : null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getTime = (u: any): string | null => u.createdAt || u.created_at || null;

const fmtDate = (t: string | null) => {
  if (!t) return null;
  try {
    return format(new Date(t), 'MMM d, h:mm a');
  } catch {
    return null;
  }
};

const fmtLoc = (
  city?: string | null,
  state?: string | null,
  zip?: string | null,
) => {
  if (city && state) return `${city}, ${state}`;
  if (city) return city;
  if (zip) return `ZIP ${zip}`;
  return null;
};

const fmtStatus = (status?: string) => {
  if (!status) return '';
  return status
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export const JourneyMap = ({
  carrierTracking,
  trackingNumber,
  className,
}: JourneyMapProps) => {
  const [copied, setCopied] = useState(false);

  const updates =
    carrierTracking?.trackingUpdates ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (carrierTracking as any)?.tracking_updates;

  if (!updates?.length) return null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const carrier =
    carrierTracking?.carrier || (carrierTracking as any)?.carrier || 'USPS';

  // Reverse for newest first
  const sorted = [...updates].reverse();

  const copyTracking = async () => {
    if (!trackingNumber) return;
    try {
      await navigator.clipboard.writeText(trackingNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = trackingNumber;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-slate-200 bg-white',
        className,
      )}
    >
      {/* Compact Header */}
      <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-3 py-2">
        <div className="flex items-center gap-2">
          <TruckSolid className="h-4 w-4 text-slate-500" />
          <span className="text-xs font-medium text-slate-700">Tracking</span>
          <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
            {carrier}
          </span>
        </div>
        {trackingNumber && (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={copyTracking}
              className={cn(
                'flex items-center gap-1 rounded px-1.5 py-0.5 font-mono text-[10px] transition-all',
                copied
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
              )}
              title="Copy tracking number"
            >
              {copied ? (
                <ClipboardDocumentCheckIcon className="h-3 w-3" />
              ) : (
                <ClipboardDocumentIcon className="h-3 w-3" />
              )}
              {trackingNumber}
            </button>
            <a
              href={`https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 rounded-md bg-blue-50 px-2 py-1 text-[11px] font-medium text-blue-600 transition-colors hover:bg-blue-100"
            >
              USPS
              <ArrowTopRightOnSquareIcon className="h-3 w-3" />
            </a>
          </div>
        )}
      </div>

      {/* Scrollable Timeline */}
      <div className="max-h-[220px] divide-y divide-slate-100 overflow-y-auto">
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        {sorted.map((u: any, i: number) => {
          const statusLower = u.status?.toLowerCase() || '';
          const isDel = statusLower.includes('delivered');
          const loc = getLoc(u);
          const locStr = fmtLoc(loc?.city, loc?.state, loc?.zip);
          const dateStr = fmtDate(getTime(u));
          const { message } = u;
          const signedBy = u.signedBy || u.signed_by;

          // Build detail parts
          const details = [message, locStr, signedBy && `Signed by ${signedBy}`]
            .filter(Boolean)
            .join(' · ');

          return (
            <div key={i} className="flex gap-3 px-3 py-2">
              {/* Dot - aligned to first line of text */}
              <div className="flex h-[18px] w-2 flex-shrink-0 items-center justify-center">
                <div
                  className={cn(
                    'h-1.5 w-1.5 rounded-full',
                    isDel ? 'bg-emerald-500' : 'bg-slate-300',
                  )}
                />
              </div>

              {/* Content */}
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span
                    className={cn(
                      'text-[13px]',
                      isDel ? 'font-medium text-emerald-700' : 'text-slate-700',
                    )}
                  >
                    {fmtStatus(u.status)}
                  </span>
                  <span className="flex-shrink-0 text-[11px] tabular-nums text-slate-400">
                    {dateStr}
                  </span>
                </div>
                {details && (
                  <p className="mt-0.5 text-[11px] text-slate-400">{details}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
